import { useEffect, useState, type ChangeEvent } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import {
  borrarAsistencia, contarCorreccionesAsistencia, corregirAsistencia, firmarFotosParaReporte, getPhotoUrl,
  listAttendanceForValidation, listCorreccionesAsistencia, listMarcasConLaMismaFoto,
  registrarAsistenciaManual, setManualExit, subirFotoDeAsistenciaManual,
  subirFotoDeCorreccionAsistencia, validateAttendance,
} from '../../api/attendance';
import { rechazoDeArchivo } from '../../lib/archivoDeFoto';
import { detalleFotoRepetida, type MarcaConFoto } from '../../domain/fotoRepetida';
import type {
  CampoCorregible, CorreccionAsistenciaRow, ValidationRow,
} from '../../api/attendance';
import { listAbsences, listTurnosParaReporte } from '../../api/positions';
import type { AbsenceRow, TurnoRow } from '../../api/positions';
import { listApprovedAuthorizations } from '../../api/authorizations';
import { listCampaignOptions, listSupervisorAssignments } from '../../api/campaigns';
import type { CampaignOption } from '../../api/campaigns';
import FilaTarjeta from '../../components/FilaTarjeta';
import { useAuth } from '../../components/AuthProvider';
import EstadoCopiaGarra from '../../components/EstadoCopiaGarra';
import { puedeEditar, soloSupervisaTiendas } from '../../domain/permissions';
import { buildAsistenciaWorkbook, reporteFileName } from '../../domain/excel/exportReportes';
import { descargarXlsx } from '../../lib/descargas';
import { coincideTodasLasPalabras, rangoDelMes } from '../../domain/tableFilters';

const TZ = 'America/Lima';
/** Perú no tiene horario de verano: el offset es -05:00 todo el año. */
const LIMA_OFFSET = '-05:00';

// El TEXTO sale de domain/flags (fuente unica); aqui solo vive el COLOR, que es
// decision de esta pantalla. Antes se duplicaban ambos y una bandera nueva salia en crudo.
import { FLAG_LABEL } from '../../domain/flags';
import TarjetasResumen from '../../components/TarjetasResumen';

const FLAG_CLS: Record<string, string> = {
  tardanza: 'bg-amber-100 text-amber-800',
  salida_temprana: 'bg-amber-100 text-amber-800',
  sin_gps: 'bg-amber-100 text-amber-800',
  lejos_del_pdv: 'bg-red-100 text-red-800',
  hora_sospechosa: 'bg-red-100 text-red-800',
  foto_repetida: 'bg-red-100 text-red-800',
};

/**
 * Debajo de la insignia «Foto repetida», CON QUIÉN comparte la foto. La bandera sola dice que la
 * foto ya se usó; para decidir hace falta saber si es la salida del mismo turno o la selfie de
 * otra persona en otra tienda.
 */
function DetalleFotoRepetida({ row, mismaFoto }: {
  row: ValidationRow; mismaFoto: MarcaConFoto[] | null;
}) {
  if (!row.flags.includes('foto_repetida')) return null;
  return (
    <span className="block w-full text-xs text-red-800">
      {detalleFotoRepetida(row, mismaFoto)}
    </span>
  );
}
const FLAG_BADGES = new Proxy({} as Record<string, { label: string; cls: string }>, {
  get: (_t, k: string) => ({
    label: FLAG_LABEL[k] ?? k,
    cls: FLAG_CLS[k] ?? 'bg-gray-100 text-gray-700',
  }),
});

/** Cómo se nombra cada estado cuando el filtro viaja al Excel. */
const ESTADO_FILTRO_LABEL: Record<string, string> = {
  pendiente: 'pendientes de validar', validada: 'validadas', observada: 'observadas',
};

const STATUS_LABEL: Record<string, string> = {
  pendiente: 'Pendiente',
  validada: 'Validada',
  observada: 'Observada',
};

/** Qué pide el modal de nota: observar el lote seleccionado o reabrir una fila ya resuelta. */
type NoteModal = { mode: 'observar' } | { mode: 'reabrir'; row: ValidationRow };

/**
 * Lo que el modal de corrección tiene en pantalla.
 *
 * Cada campo va con su propia casilla `cambiar*`: es la única forma de que corregir SOLO la foto no
 * arrastre la hora ni el comentario. El diff que viaja lleva los campos MARCADOS y que además
 * cambiaron —mandar un campo con su mismo valor escribiría una fila de rastro que no corrige nada—.
 */
interface FormCorreccion {
  cambiarHora: boolean;   hora: string;
  cambiarTipo: boolean;   tipo: 'ingreso' | 'salida';
  cambiarFoto: boolean;   foto: File | null;
  cambiarNota: boolean;   nota: string;
  motivo: string;
}

function formInicial(row: ValidationRow): FormCorreccion {
  return {
    cambiarHora: false, hora: formatInTimeZone(row.device_ts, TZ, 'HH:mm'),
    cambiarTipo: false, tipo: row.kind,
    cambiarFoto: false, foto: null,
    cambiarNota: false, nota: row.validation_note ?? '',
    motivo: '',
  };
}

/**
 * Lo que la ventana de «completar turno» tiene en pantalla.
 *
 * Puede pedir UNA marca (al turno le falta solo el ingreso o solo la salida) o LAS DOS (el turno
 * salió como falta y no tiene ninguna). Por eso cada sección lleva su `activo`: la ventana se abre
 * con activas las que de verdad faltan, y las que ya existen no se pueden ni tocar.
 *
 * El motivo es UNO para las dos: es la misma explicación —«no había señal en sala», «se le olvidó
 * marcar»— y pedirla dos veces solo consigue que la segunda diga «lo mismo».
 */
interface ManualModal {
  assignmentId: string;
  fecha: string;
  pdv: string;
  trabajador: string;
  /** Qué marcas faltan de verdad. La ventana no ofrece crear una que ya existe. */
  faltan: ('ingreso' | 'salida')[];
  horaIngreso: string;
  horaSalida: string;
}

interface FormManual {
  ingreso: { activo: boolean; hora: string; foto: File | null };
  salida: { activo: boolean; hora: string; foto: File | null };
  motivo: string;
}

/** El texto de cada campo en el historial. Sin esto el rastro muestra el nombre de la columna. */
const CAMPO_LABEL: Record<string, string> = {
  device_ts: 'Hora', photo_path: 'Foto', validation_note: 'Comentario', kind: 'Tipo de marca',
};

export default function Validacion() {
  const { roles } = useAuth();
  /**
   * Quién ve el botón de corregir: coordinador, datos y supervisor (0228). Quién puede corregir CADA
   * fila lo decide el servidor (`puede_editar_asistencia` + `corrige_marcas_por_rol`): el supervisor
   * solo en sus tiendas, nadie sus propias marcas. Esto es encuadre, no seguridad.
   */
  const puedeCorregir = puedeEditar('validacion', roles);
  /** El supervisor puro ve solo sus tiendas y no descarga (decisión del usuario, 25-set). */
  const soloTiendas = soloSupervisaTiendas(roles);
  /**
   * Borrar es del jefe y de nadie más. No es la misma llave que `puedeCorregir`: corregir deja
   * rastro a la vista y borrar no deja nada en pantalla, así que el servidor lo cierra a `admin`
   * (la RPC responde "Solo un administrador puede borrar una marca"). Si acá dijera
   * `coordinador`, el botón le aparecería a quien después se come el rechazo.
   */
  const esJefe = roles.includes('admin');
  const today = formatInTimeZone(new Date(), TZ, 'yyyy-MM-dd');
  const monthStart = today.slice(0, 8) + '01';
  const [campaigns, setCampaigns] = useState<CampaignOption[]>([]);
  const [campaignId, setCampaignId] = useState('');
  const [dateFrom, setDateFrom] = useState(monthStart);
  const [dateTo, setDateTo] = useState(today);
  const [rows, setRows] = useState<ValidationRow[]>([]);
  const [absences, setAbsences] = useState<AbsenceRow[]>([]);
  /**
   * Toda la programación del rango. NO se pinta en pantalla: alimenta solo el Excel y el contador
   * del botón de descarga. Ver el comentario de `descargarReporte`.
   */
  const [turnos, setTurnos] = useState<TurnoRow[]>([]);
  /** claves `${workerId}|${date}` con falta/permiso aprobado */
  const [justified, setJustified] = useState<Set<string>>(new Set());
  /** claves `${workerId}|${date}` con autorización de TARDANZA aprobada */
  const [lateJustified, setLateJustified] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [onlyAlerts, setOnlyAlerts] = useState(false);
  /** Mes elegido (`yyyy-MM`). No filtra por su cuenta: fija el rango y vuelve a buscar. */
  const [mes, setMes] = useState('');
  /** Texto libre: nombre, tienda o documento. Filtra en el navegador, sin volver a la base. */
  const [busqueda, setBusqueda] = useState('');
  /** `todos` no es un estado de la marca: es «no filtres por estado». */
  const [estado, setEstado] = useState<'todos' | 'pendiente' | 'validada' | 'observada'>('todos');
  const [noteModal, setNoteModal] = useState<NoteModal | null>(null);
  const [note, setNote] = useState('');
  /** La marca que se está corrigiendo, y el formulario del modal. */
  const [editRow, setEditRow] = useState<ValidationRow | null>(null);
  const [form, setForm] = useState<FormCorreccion | null>(null);
  /** Cuántas correcciones tiene cada marca: alimenta la etiqueta «Editado» de la tabla. */
  const [correcciones, setCorrecciones] = useState<Map<string, number>>(new Map());
  /**
   * Las marcas que comparten foto con alguna de las que llegaron con `foto_repetida`, para decir
   * CUÁL es la otra. `null` = la lectura falló (se dice así, no se inventa que no hay ninguna).
   */
  const [mismaFoto, setMismaFoto] = useState<MarcaConFoto[] | null>([]);
  /** Motivo por el que se rechazó una foto elegida en los modales, por campo. */
  const [rechazoFoto, setRechazoFoto] = useState<Record<string, string>>({});
  /** El rastro de la marca abierta en el modal. */
  const [historial, setHistorial] = useState<CorreccionAsistenciaRow[]>([]);
  /** El turno que se está completando a mano, y su formulario. */
  const [manual, setManual] = useState<ManualModal | null>(null);
  const [formManual, setFormManual] = useState<FormManual | null>(null);
  /** fila de INGRESO cuyo turno quedó sin salida, para fijar la hora justificada */
  const [borrarModal, setBorrarModal] = useState<ValidationRow | null>(null);
  const [motivoBorrado, setMotivoBorrado] = useState('');
  const [exitModal, setExitModal] = useState<ValidationRow | null>(null);
  const [exitTime, setExitTime] = useState('');
  const [exitNote, setExitNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  /**
   * Quién supervisa cada tienda EN ESTA CAMPAÑA (pestaña «Supervisores», `campaign_pdv_supervisors`):
   * pdv_id → nombre. Alimenta el filtro por supervisor, que pidió el usuario para ver el avance de
   * cada uno. `SIN_SUPERVISOR` es la opción de las tiendas que todavía no tienen a nadie.
   */
  const [supervisorDe, setSupervisorDe] = useState<Map<string, string>>(new Map());
  const [supervisor, setSupervisor] = useState('');

  /**
   * Solo las campañas VIGENTES en el rango que se está mirando. Se recarga cuando cambia el rango:
   * si se mira agosto, salen las de agosto — aunque hoy sea setiembre y ya hayan terminado.
   *
   * RLS: el coordinador solo recibe las campañas donde es coordinator_id; el admin ve todas.
   */
  useEffect(() => {
    listCampaignOptions(dateFrom, dateTo)
      .then((cs) => {
        setCampaigns(cs);
        // Si la elegida ya no está en la lista, se suelta: dejarla seleccionada mostraría
        // resultados de una campaña que el desplegable ya no ofrece.
        setCampaignId((actual) => (cs.some((c) => c.id === actual) ? actual : ''));
      })
      .catch(() => setMessage('No se pudieron cargar las campañas.'));
  }, [dateFrom, dateTo]);

  /**
   * El reparto de supervisores se pide al cambiar de campaña. Se suelta el filtro: un supervisor de
   * otra campaña dejaría la tabla vacía sin decir por qué. Si la lectura falla, el filtro no aparece
   * (no es motivo para tumbar la validación), pero se dice.
   */
  useEffect(() => {
    setSupervisor('');
    if (!campaignId || soloTiendas) { setSupervisorDe(new Map()); return; }
    let vigente = true;
    listSupervisorAssignments(campaignId)
      .then((xs) => {
        if (!vigente) return;
        // Bajo RLS un embed sin permiso llega en null sin error: esa tienda cae en «sin supervisor».
        setSupervisorDe(new Map(xs
          .filter((x) => x.profile != null)
          .map((x) => [x.pdv_id, `${x.profile.first_names} ${x.profile.last_names}`.trim()])));
      })
      .catch(() => {
        if (!vigente) return;
        setSupervisorDe(new Map());
        setMessage('No se pudo cargar quién supervisa cada tienda: el filtro por supervisor no está disponible.');
      });
    return () => { vigente = false; };
  }, [campaignId, soloTiendas]);

  /**
   * Elegir un mes busca solo, sin tocar «Buscar»: es el atajo que pidió el usuario.
   *
   * Va en un efecto y no dentro de `elegirMes` porque `setDateFrom`/`setDateTo` no se aplican hasta
   * el siguiente render: llamar a `buscar()` ahí mismo consultaría el rango VIEJO y traería el mes
   * anterior, que es el peor resultado posible — parece que funcionó y trae otro dato.
   */
  useEffect(() => {
    if (mes === '' || !campaignId) return;
    void buscar();
    // `buscar` se redefine en cada render; la dependencia que importa es el mes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mes, dateFrom, dateTo]);

  async function buscar() {
    if (!campaignId) { setMessage('Selecciona una campaña.'); return; }
    setLoading(true);
    setMessage('');
    try {
      // Los turnos se piden acá aunque la pantalla no los muestre: sin conocerlos, el botón de
      // descarga no puede saber si hay algo que bajar y quedaba deshabilitado justo en el caso
      // que el usuario pidió —bajar la plantilla de programación vacía, antes de que nadie marque—.
      // Traerlos en `buscar()` además evita repetir la consulta al descargar.
      const [att, abs, tur] = await Promise.all([
        listAttendanceForValidation(campaignId, dateFrom, dateTo),
        listAbsences(campaignId, dateFrom, dateTo),
        listTurnosParaReporte(campaignId, dateFrom, dateTo),
      ]);
      setRows(att);
      setAbsences(abs);
      setTurnos(tur);
      setSelected(new Set());
      // La etiqueta «Editado» se pregunta por el lote: fila por fila serían 500 viajes.
      setCorrecciones(await contarCorreccionesAsistencia(att.map((r) => r.id)));

      // Con quién comparte la foto cada marca con `foto_repetida`. Si la lectura falla, la
      // validación sigue: la bandera se ve igual y el detalle dice que no se pudo cargar.
      const huellas = att
        .filter((r) => r.flags.includes('foto_repetida') && r.foto_huella)
        .map((r) => r.foto_huella as string);
      try {
        setMismaFoto(huellas.length === 0 ? [] : await listMarcasConLaMismaFoto(huellas));
      } catch {
        setMismaFoto(null);
      }

      // Se cruzan las autorizaciones de TODOS los trabajadores del rango (no solo los
      // de las faltas): las de tipo `tardanza` justifican el flag de una asistencia.
      const workerIds = [...new Set([
        ...att.map((r) => r.assignment.worker?.id ?? r.assignment.worker_id)
          .filter((v): v is string => !!v),
        ...abs.map((a) => a.worker?.id).filter((v): v is string => !!v),
      ])];
      const auths = await listApprovedAuthorizations(workerIds, dateFrom, dateTo);
      setJustified(new Set(
        auths
          .filter((a) => a.kind === 'falta' || a.kind === 'permiso')
          .map((a) => `${a.worker_id}|${a.event_date}`),
      ));
      setLateJustified(new Set(
        auths
          .filter((a) => a.kind === 'tardanza')
          .map((a) => `${a.worker_id}|${a.event_date}`),
      ));
    } catch (e) {
      setMessage('Error al cargar: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setLoading(false);
    }
  }

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  }

  /**
   * Marca o desmarca TODO lo que está a la vista.
   *
   * "A la vista" y no "todo lo cargado": la pantalla tiene filtros, y quien acaba de filtrar por
   * una campaña o por alertas espera validar eso y no lo que quedó escondido. Validar en lote algo
   * que no se está viendo es aprobar a ciegas marcados que impactan el pago.
   *
   * Solo entran las `pendiente`: las ya resueltas no se vuelven a validar, y meterlas en la
   * selección haría que el contador del botón mintiera.
   */
  function alternarTodas(marcar: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const r of seleccionables) {
        if (marcar) { next.add(r.id); } else { next.delete(r.id); }
      }
      return next;
    });
  }

  function cerrarNota() {
    setNoteModal(null);
    setNote('');
  }

  async function aplicar(newStatus: 'validada' | 'observada', n: string) {
    try {
      const count = await validateAttendance([...selected], newStatus, n);
      cerrarNota();
      await buscar(); // primero recargar: `buscar()` limpia el mensaje al empezar
      setMessage(`${count} asistencia(s) actualizada(s).`);
    } catch (e) {
      setMessage('Error al actualizar: ' + (e instanceof Error ? e.message : String(e)));
    }
  }

  /** Devuelve una asistencia ya validada u observada al estado `pendiente` (con nota y auditoría). */
  async function reabrir(row: ValidationRow, n: string) {
    try {
      const count = await validateAttendance([row.id], 'pendiente', n);
      cerrarNota();
      await buscar();
      setMessage(
        count === 1
          ? 'Validación reabierta: la asistencia volvió a "Pendiente".'
          : 'No se pudo reabrir la asistencia (¿el cierre del periodo ya fue aprobado?).',
      );
    } catch (e) {
      setMessage('Error al reabrir: ' + (e instanceof Error ? e.message : String(e)));
    }
  }

  function confirmarNota() {
    if (!noteModal) return;
    const n = note.trim();
    if (n === '') return;
    if (noteModal.mode === 'observar') void aplicar('observada', n);
    else void reabrir(noteModal.row, n);
  }

  function abrirBorrado(row: ValidationRow) {
    setBorrarModal(row);
    setMotivoBorrado('');
  }

  /**
   * Borra la marca de verdad, no la tacha.
   *
   * Pedido del usuario (2026-09-09): cuando la impulsadora registra mal, no es un dato con
   * matices sino un error total, y un registro anulado a la vista sigue invitando a preguntar
   * qué pasó. Lo que queda después es la asignación: el turno se lee como falta y el recurso
   * puede volver a marcar, que es justo lo que el UNIQUE de una marca por tipo le impedía
   * mientras la equivocada siguiera ahí.
   *
   * El rastro vive adentro: un disparador copia la fila entera al `audit_log` antes del borrado
   * y la RPC guarda el motivo aparte. En pantalla no queda nada; en la auditoría, todo.
   */
  async function confirmarBorrado() {
    if (!borrarModal) return;
    const motivo = motivoBorrado.trim();
    if (motivo === '') return;
    const id = borrarModal.id;
    setLoading(true);
    try {
      await borrarAsistencia(id, motivo);
      setBorrarModal(null);
      setMotivoBorrado('');
      // Fuera de la selección: si quedaba marcada, el contador del botón de validar contaría
      // una asistencia que ya no existe.
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      await buscar();
      setMessage('Registro eliminado. El turno queda como falta y el recurso puede volver a marcar.');
    } catch (e) {
      setMessage('Error al eliminar: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setLoading(false);
    }
  }

  function abrirSalida(row: ValidationRow) {
    setExitModal(row);
    setExitTime(row.assignment.position.end_time.slice(0, 5));
    setExitNote('');
  }

  /** Fija la hora de salida que el recurso no marcó: RPC auditada, nunca un INSERT directo. */
  async function registrarSalida() {
    if (!exitModal) return;
    const motivo = exitNote.trim();
    if (exitTime === '' || motivo === '') return;
    const deviceTs = `${exitModal.assignment.position.date}T${exitTime}:00${LIMA_OFFSET}`;
    setLoading(true);
    try {
      await setManualExit(exitModal.assignment.id, deviceTs, motivo);
      setExitModal(null);
      setExitNote('');
      await buscar();
      setMessage('Salida justificada registrada (queda auditada y cuenta en el cierre).');
    } catch (e) {
      setMessage('Error al registrar la salida: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setLoading(false);
    }
  }

  async function verFoto(path: string) {
    try {
      window.open(await getPhotoUrl(path), '_blank');
    } catch {
      setMessage('No se pudo obtener la foto.');
    }
  }

  /**
   * Abre la ventana para completar un turno. `faltan` decide qué secciones vienen activas, así que
   * el mismo formulario sirve para el hueco de media marca y para el turno entero.
   */
  /**
   * La foto que eligió el coordinador, si se puede subir (tipo y tope del bucket, 0314); si no,
   * `null` y el motivo queda a la vista bajo el campo. Acá la foto se sube TAL CUAL, sin comprimir
   * —suele ser una captura de WhatsApp—, así que lo que se mide es lo que viaja.
   */
  function fotoQueSirve(clave: string, e: ChangeEvent<HTMLInputElement>): File | null {
    const f = e.target.files?.[0] ?? null;
    const motivo = f === null ? null : rechazoDeArchivo(f);
    setRechazoFoto((prev) => {
      const n = { ...prev };
      if (motivo === null) { delete n[clave]; } else { n[clave] = motivo; }
      return n;
    });
    if (motivo !== null) { e.target.value = ''; return null; }
    return f;
  }

  function abrirManual(m: ManualModal) {
    setManual(m);
    setRechazoFoto({});
    setFormManual({
      ingreso: { activo: m.faltan.includes('ingreso'), hora: m.horaIngreso, foto: null },
      salida: { activo: m.faltan.includes('salida'), hora: m.horaSalida, foto: null },
      motivo: '',
    });
  }

  function cerrarManual() {
    setManual(null);
    setFormManual(null);
  }

  /**
   * Crea las marcas que faltan, una llamada por marca.
   *
   * Van de a una y no en una sola transacción a propósito: si la segunda falla, la primera ya entró
   * y eso es mejor que perder las dos —el turno queda a medio completar y se ve, mientras que un
   * todo-o-nada obligaría a volver a subir las dos fotos—. El mensaje dice cuántas entraron.
   */
  async function guardarManual() {
    if (!manual || !formManual) return;
    const motivo = formManual.motivo.trim();
    if (motivo === '') return;

    const pedidos: { kind: 'ingreso' | 'salida'; hora: string; foto: File }[] = [];
    for (const kind of ['ingreso', 'salida'] as const) {
      const sec = formManual[kind];
      if (sec.activo && sec.foto) pedidos.push({ kind, hora: sec.hora, foto: sec.foto });
    }
    if (pedidos.length === 0) {
      setMessage('Falta la foto: un registro a mano no se guarda sin su evidencia.');
      return;
    }

    setLoading(true);
    let hechas = 0;
    try {
      for (const p of pedidos) {
        // La FECHA sale de la asignación, no del formulario: es el asiento del registro.
        const deviceTs = `${manual.fecha}T${p.hora}:00${LIMA_OFFSET}`;
        const ruta = await subirFotoDeAsistenciaManual(manual.assignmentId, p.foto);
        await registrarAsistenciaManual(manual.assignmentId, p.kind, deviceTs, ruta, motivo);
        hechas += 1;
      }
      cerrarManual();
      await buscar();
      setMessage(
        `${hechas === 1 ? 'Marca creada' : `${hechas} marcas creadas`} a mano. `
        + 'Quedan con la etiqueta «Manual», tu usuario y el motivo, y entran al cierre.',
      );
    } catch (e) {
      await buscar();
      setMessage(
        (hechas > 0 ? `Se crearon ${hechas} de ${pedidos.length}. ` : '')
        + 'Error al registrar: ' + (e instanceof Error ? e.message : String(e)),
      );
    } finally {
      setLoading(false);
    }
  }

  async function abrirEdicion(row: ValidationRow) {
    setEditRow(row);
    setRechazoFoto({});
    setForm(formInicial(row));
    setHistorial([]);
    try {
      setHistorial(await listCorreccionesAsistencia(row.id));
    } catch {
      // El historial es contexto, no requisito: si no carga, la corrección igual se puede hacer.
    }
  }

  function cerrarEdicion() {
    setEditRow(null);
    setForm(null);
    setHistorial([]);
  }

  /**
   * Manda SOLO lo marcado y que además cambió.
   *
   * La foto se sube ANTES de llamar a la RPC, y eso deja un caso que conviene tener presente: si la
   * subida entra y la RPC falla (motivo vacío, RLS, red), el archivo queda en el bucket sin que
   * nadie lo referencie. Es basura, no un dato perdido — la alternativa sería subirla después, y
   * entonces la marca apuntaría a una ruta que todavía no existe.
   */
  async function guardarCorreccion() {
    if (!editRow || !form) return;
    const motivo = form.motivo.trim();
    if (motivo === '') return;

    const cambios: Partial<Record<CampoCorregible, string>> = {};

    if (form.cambiarHora) {
      // La FECHA no se toca: sale de la asignación. Acá solo se corrige la hora, con el offset
      // fijo de Lima. Ver el bloque 1 de la 0074.
      const nuevo = `${editRow.assignment.position.date}T${form.hora}:00${LIMA_OFFSET}`;
      if (formatInTimeZone(nuevo, TZ, 'HH:mm') !== formatInTimeZone(editRow.device_ts, TZ, 'HH:mm')) {
        cambios.device_ts = nuevo;
      }
    }
    if (form.cambiarTipo && form.tipo !== editRow.kind) cambios.kind = form.tipo;
    if (form.cambiarNota && form.nota.trim() !== (editRow.validation_note ?? '')) {
      cambios.validation_note = form.nota.trim();
    }

    setLoading(true);
    try {
      if (form.cambiarFoto && form.foto) {
        cambios.photo_path = await subirFotoDeCorreccionAsistencia(
          editRow.assignment.id, form.foto,
        );
      }
      if (Object.keys(cambios).length === 0) {
        setMessage('No marcaste ningún cambio, o lo marcado quedó igual que estaba.');
        return;
      }
      await corregirAsistencia(editRow.id, cambios, motivo);
      cerrarEdicion();
      await buscar();
      const n = Object.keys(cambios).length;
      setMessage(`Corrección guardada: ${n} ${n === 1 ? 'campo' : 'campos'}. Queda con tu usuario, `
        + 'el valor anterior y el motivo en el historial de la marca.');
    } catch (e) {
      setMessage('Error al corregir: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setLoading(false);
    }
  }

  /**
   * El texto se busca en nombre, apellidos, documento y tienda: es lo que el usuario tiene a mano.
   *
   * El motor vive en `domain/tableFilters.ts` y no aca. Se movio cuando la bandeja de cierres pidio
   * «la casilla de filtro que tambien tiene la pestana de validacion»: dos casillas que se ven
   * iguales y se comportan distinto es peor que no tener la segunda.
   */
  function coincide(
    worker: { dni: string; first_names: string; last_names: string } | null,
    pdv: { name: string } | null,
  ): boolean {
    return coincideTodasLasPalabras(
      busqueda, worker?.first_names, worker?.last_names, worker?.dni, pdv?.name,
    );
  }

  /**
   * Lo que se ve, y por lo tanto lo que se descarga: la hoja `Marcados` del Excel usa esta misma
   * lista. Los tres filtros se acumulan; `meta.filtros` deja escrito cuáles estaban puestos.
   */
  /**
   * El filtro por supervisor va por TIENDA: la pestaña «Supervisores» reparte tiendas, no personas.
   * Se aplica igual a marcas, faltas y turnos, para que las seis tarjetas cuenten el avance de ese
   * supervisor y no mezclen sus números con los de la campaña.
   */
  const SIN_SUPERVISOR = '__sin__';
  const pasaSupervisor = (pdvId: string | undefined): boolean => {
    if (supervisor === '') return true;
    const quien = pdvId ? supervisorDe.get(pdvId) : undefined;
    return supervisor === SIN_SUPERVISOR ? quien === undefined : quien === supervisor;
  };
  const supervisores = [...new Set(supervisorDe.values())].sort((a, b) => a.localeCompare(b, 'es'));
  const haySinSupervisor = turnos.some((t) => !t.pdvId || !supervisorDe.has(t.pdvId));

  const visible = rows.filter((r) => {
    if (onlyAlerts && r.flags.length === 0) return false;
    if (estado !== 'todos' && r.status !== estado) return false;
    if (!pasaSupervisor(r.assignment.position.pdv_id)) return false;
    return coincide(r.assignment.worker, r.assignment.position.pdv);
  });

  /**
   * Las faltas solo se filtran por texto y supervisor: no tienen estado de validación ni alertas,
   * así que filtrarlas por esos dos las haría desaparecer sin que el usuario entienda por qué.
   */
  const faltasVisibles = absences.filter((a) => pasaSupervisor(a.pdvId) && coincide(a.worker, a.pdv));
  /** La programación del rango, recortada SOLO por supervisor: es el «de cuánto» de su avance. */
  const turnosVisibles = turnos.filter((t) => pasaSupervisor(t.pdvId));

  /** Un mes elegido se traduce a rango y se busca de una vez: es el atajo, no un filtro aparte. */
  function elegirMes(valor: string) {
    setMes(valor);
    const r = rangoDelMes(valor);
    if (!r) return;
    setDateFrom(r.desde);
    setDateTo(r.hasta);
  }

  /**
   * Arma el Excel con DOS alcances distintos, a propósito:
   *
   * - hoja `Marcados`: EXACTAMENTE lo que está a la vista (`visible`, ya filtrado). Un botón que
   *   dice "descargar" al lado de una lista filtrada y entrega otra cosa es la peor clase de
   *   error: el archivo se ve bien y nadie descubre que sobra o falta hasta que alguien cuadra
   *   los números contra el cliente.
   * - hoja `Turnos`: toda la programación del rango, se haya marcado o no. Es lo que pidió el
   *   usuario — el archivo se usa como plantilla de programación y un turno incumplido tiene que
   *   quedar en su línea, en cero. El `Resumen` deja escrita esa diferencia de alcance.
   *
   * Los turnos ya vienen cargados de `buscar()` (estado `turnos`) y **no se pintan en pantalla**:
   * la lista sigue mostrando como faltas solo las de fechas pasadas (`listAbsences`). Llenar la
   * pantalla de "faltas" del día en curso, a media jornada, sería un error de producto: el
   * coordinador vería en rojo turnos que la gente todavía está trabajando. En el archivo eso se
   * resuelve distinto —con los cumplimientos `Programado` y `En curso`— porque ahí sí hacen falta
   * los turnos futuros: el Excel se usa como plantilla para llenar.
   */
  async function descargarReporte() {
    // Con un supervisor elegido, la hoja «Turnos» también es solo la de sus tiendas: un archivo
    // que dice «de Verona» y trae la programación de las 42 no cuadra con nada.
    const turnos = turnosVisibles;
    if (turnos.length === 0 && visible.length === 0) {
      setMessage('No hay nada que descargar: ese rango no tiene turnos programados ni marcados.');
      return;
    }
    const c = campaigns.find((x) => x.id === campaignId);
    const meta = {
      campaignName: c ? `${c.client} — ${c.brand}` : 'Campaña',
      periodStart: dateFrom,
      periodEnd: dateTo,
      // El archivo tiene que decir con qué filtros se armó: la hoja `Marcados` es `visible`, y
      // un Excel que no declara su recorte se cuadra mal contra el cliente.
      filtros: [
        ...(onlyAlerts ? ['Solo marcados con alguna alerta'] : []),
        ...(estado !== 'todos' ? [`Solo ${ESTADO_FILTRO_LABEL[estado]}`] : []),
        ...(busqueda.trim() !== '' ? [`Búsqueda: «${busqueda.trim()}»`] : []),
        ...(supervisor === SIN_SUPERVISOR ? ['Solo tiendas sin supervisor asignado']
          : supervisor !== '' ? [`Solo las tiendas de ${supervisor}`] : []),
      ],
    };
    setLoading(true);
    setMessage('Preparando el reporte y firmando los enlaces de las fotos…');
    try {
      // Se firman las fotos de TODO lo que va al archivo, no solo las de la lista visible: las
      // marcas que llegan por la hoja de turnos pueden no estar en pantalla (el filtro "Solo con
      // alertas" las esconde) y sin su firma esas celdas saldrían vacías, como si no hubiera foto.
      const enlaces = await firmarFotosParaReporte([
        ...visible.map((r) => r.photo_path),
        ...turnos.flatMap((t) => [t.ingreso?.photo_path, t.salida?.photo_path])
          .filter((p): p is string => typeof p === 'string'),
      ]);
      descargarXlsx(
        // Las justificaciones ya cruzadas viajan al archivo: sin ellas el Excel decía «Falta» a
        // secas de alguien a quien la pantalla pinta «Falta justificada» en verde y a quien el
        // cierre le paga el día completo. De las tres respuestas, la única que salía del sistema
        // por correo era la que acusaba a la persona.
        buildAsistenciaWorkbook(meta, turnos, visible, enlaces,
          { faltas: justified, tardanzas: lateJustified }),
        reporteFileName('asistencia', meta),
      );
      setMessage(
        `Excel descargado: ${turnos.length} turno(s) en la hoja «Turnos» (toda la programación `
        + `del rango) y ${visible.length} marcado(s) en «Marcados» (lo que está a la vista). `
        + 'Los enlaces de foto abren sin iniciar sesión y vencen en un año: el archivo lleva '
        + 'evidencia de personas, trátalo como reservado.',
      );
    } catch (e) {
      setMessage('No se pudo generar el Excel: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setLoading(false);
    }
  }
  /** Lo que se puede validar de lo que está a la vista: las ya resueltas no cuentan. */
  const seleccionables = visible.filter((r) => r.status === 'pendiente');
  const todasMarcadas = seleccionables.length > 0
    && seleccionables.every((r) => selected.has(r.id));
  /** Asignaciones que ya tienen marcado de salida: el resto de sus ingresos quedó "Sin salida". */
  const conSalida = new Set(rows.filter((r) => r.kind === 'salida').map((r) => r.assignment.id));
  const sinSalida = (r: ValidationRow): boolean =>
    r.kind === 'ingreso' && !conSalida.has(r.assignment.id);
  /**
   * El espejo del anterior, que faltaba: una SALIDA cuya asignación no tiene ingreso.
   *
   * Pasa cuando la impulsadora no pudo marcar al llegar —hay tiendas que no dejan usar el celular
   * en sala— y sí al salir. Sin esta alerta ese turno se veía completo con media marca.
   */
  const conIngreso = new Set(rows.filter((r) => r.kind === 'ingreso').map((r) => r.assignment.id));
  const sinIngreso = (r: ValidationRow): boolean =>
    r.kind === 'salida' && !conIngreso.has(r.assignment.id);
  const tardanzaJustificada = (r: ValidationRow): boolean =>
    r.flags.includes('tardanza') &&
    lateJustified.has(`${r.assignment.worker?.id ?? r.assignment.worker_id}|${r.assignment.position.date}`);

  return (
    <div className="p-6">
      <h1 className="mb-4 text-xl font-semibold">Validación de asistencias</h1>

      <EstadoCopiaGarra campaignId={campaignId} />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-sm">
          Campaña
          <select className="rounded border px-2 py-1" value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}>
            <option value="">— Selecciona —</option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>{c.client} — {c.brand}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col text-sm">
          Mes
          <input type="month" className="rounded border px-2 py-1" value={mes}
            onChange={(e) => elegirMes(e.target.value)} />
        </label>
        <label className="flex flex-col text-sm">
          Desde
          <input type="date" className="rounded border px-2 py-1" value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setMes(''); }} />
        </label>
        <label className="flex flex-col text-sm">
          Hasta
          <input type="date" className="rounded border px-2 py-1" value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setMes(''); }} />
        </label>
        <button onClick={buscar} disabled={loading}
          className="rounded bg-blue-600 px-4 py-1.5 text-white disabled:opacity-50">
          {loading ? 'Cargando…' : 'Buscar'}
        </button>
        <label className="flex flex-col text-sm">
          Nombre, tienda o documento
          <input type="search" className="w-56 rounded border px-2 py-1" value={busqueda}
            placeholder="Ej.: Huamani, Surco, 46053436"
            onChange={(e) => setBusqueda(e.target.value)} />
        </label>
        <label className="flex flex-col text-sm">
          Estado
          <select className="rounded border px-2 py-1" value={estado}
            onChange={(e) => setEstado(
              e.target.value as 'todos' | 'pendiente' | 'validada' | 'observada',
            )}>
            <option value="todos">Todos</option>
            <option value="pendiente">Faltan validar</option>
            <option value="validada">Validados</option>
            <option value="observada">Observados</option>
          </select>
        </label>
        {/* Para ver el avance de cada supervisor. Sale del reparto de la pestaña «Supervisores» de la
            campaña; al supervisor puro no se le muestra, porque ya ve solo lo suyo. */}
        {!soloTiendas && supervisorDe.size > 0 && (
          <label className="flex flex-col text-sm">
            Supervisor
            <select className="rounded border px-2 py-1" value={supervisor}
              onChange={(e) => setSupervisor(e.target.value)}>
              <option value="">Todos</option>
              {supervisores.map((s) => <option key={s} value={s}>{s}</option>)}
              {haySinSupervisor && <option value={SIN_SUPERVISOR}>Sin supervisor asignado</option>}
            </select>
          </label>
        )}
        <label className="flex items-center gap-1 text-sm">
          <input type="checkbox" checked={onlyAlerts}
            onChange={(e) => setOnlyAlerts(e.target.checked)} />
          Solo con alertas
        </label>
        {/* El contador muestra los TURNOS programados, no los marcados a la vista: es el número
            de filas de la hoja `Turnos`, la que siempre viene completa y la que se usa como
            plantilla. Con `visible.length` el botón decía "(0)" y se deshabilitaba justo en el
            caso que el usuario pidió —bajar la plantilla de un rango donde todavía nadie marcó—.
            El cuántos marcados van en la otra hoja se dice al terminar la descarga, que es cuando
            el dato importa. */}
        {/* El supervisor no descarga (decisión del usuario): el Excel lleva enlaces de foto que abren
            sin iniciar sesión y el DNI de cada persona. */}
        {!soloTiendas && (
          <button type="button" onClick={() => void descargarReporte()}
            title={'Descarga la programación del rango (hoja «Turnos») y los marcados que están '
              + 'a la vista (hoja «Marcados»).'}
            disabled={loading || (turnosVisibles.length === 0 && visible.length === 0)}
            className="rounded border border-gray-300 px-4 py-1.5 text-sm disabled:opacity-50">
            Descargar Excel ({turnosVisibles.length} turno{turnosVisibles.length === 1 ? '' : 's'})
          </button>
        )}
      </div>

      {selected.size > 0 && (
        <div className="mb-3 flex gap-2">
          <button onClick={() => aplicar('validada', '')}
            className="rounded bg-green-600 px-3 py-1 text-white">
            Validar ({selected.size})
          </button>
          <button onClick={() => { setNote(''); setNoteModal({ mode: 'observar' }); }}
            className="rounded bg-amber-600 px-3 py-1 text-white">
            Observar ({selected.size})
          </button>
        </div>
      )}

      {message && <p className="mb-3 text-sm text-gray-700">{message}</p>}

      {/* «Turnos del rango» es la programación completa —la hoja `Turnos` del Excel— y los otros
          cinco cuentan lo que está a la vista con los filtros puestos. Los dos alcances conviven a
          propósito y por eso el detalle lo dice: es la misma distinción que ya llevaba el archivo,
          y confundirla es como se cuadra mal un mes contra el cliente. */}
      {(rows.length > 0 || turnos.length > 0 || absences.length > 0) && (
        <TarjetasResumen indicadores={[
          { titulo: 'Turnos del rango', valor: turnosVisibles.length,
            detalle: turnosVisibles.length === turnos.length ? 'programados' : `de ${turnos.length} programados` },
          { titulo: 'Marcados a la vista', valor: visible.length,
            detalle: visible.length === rows.length ? undefined : `de ${rows.length}` },
          { titulo: 'Faltan validar', valor: visible.filter((r) => r.status === 'pendiente').length,
            tono: 'atencion' },
          { titulo: 'Con alertas', valor: visible.filter((r) => r.flags.length > 0).length,
            tono: 'malo' },
          { titulo: 'Faltas', valor: faltasVisibles.length, tono: 'malo',
            detalle: 'de fechas pasadas' },
          { titulo: 'Editados',
            valor: visible.filter((r) => (correcciones.get(r.id) ?? 0) > 0).length,
            detalle: 'tienen corrección' },
        ]} />
      )}

      {/* En celular no hay cabecera de tabla donde colgar el "marcar todas", así que va como una
          línea propia arriba de las tarjetas. Sin esto, seleccionar 40 marcados en un teléfono es
          40 toques. */}
      {seleccionables.length > 0 && (
        <label className="mb-2 flex min-h-[44px] items-center gap-2 rounded border bg-gray-50 px-3 text-sm lg:hidden">
          <input
            type="checkbox"
            aria-label="Seleccionar todas las asistencias de la lista"
            checked={todasMarcadas}
            onChange={(e) => alternarTodas(e.target.checked)}
          />
          Seleccionar las {seleccionables.length} de la lista
        </label>
      )}

      {/* En celular: una tarjeta por marcado (y por falta), sin desplazamiento horizontal. */}
      <div className="space-y-2 lg:hidden">
        {visible.map((r) => (
          <FilaTarjeta
            key={r.id}
            titulo={`${r.assignment.worker.first_names} ${r.assignment.worker.last_names}`}
            seleccion={
              r.status === 'pendiente' ? (
                <label className="flex min-h-[44px] min-w-[44px] items-center justify-center">
                  <input type="checkbox" aria-label="Seleccionar asistencia"
                    checked={selected.has(r.id)} onChange={() => toggle(r.id)} />
                </label>
              ) : undefined
            }
            estados={
              <>
                {r.flags.map((f) => {
                  const b = FLAG_BADGES[f] ?? { label: f, cls: 'bg-gray-100 text-gray-800' };
                  return (
                    <span key={f} className={`rounded px-1.5 py-0.5 text-xs ${b.cls}`}>
                      {b.label}
                    </span>
                  );
                })}
                {tardanzaJustificada(r) && (
                  <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-800">
                    Tardanza justificada
                  </span>
                )}
                <DetalleFotoRepetida row={r} mismaFoto={mismaFoto} />
                {sinSalida(r) && (
                  <span className="rounded bg-gray-300 px-1.5 py-0.5 text-xs text-gray-800">
                    Sin salida
                  </span>
                )}
              </>
            }
            campos={[
              { etiqueta: 'Fecha', valor: r.assignment.position.date },
              { etiqueta: 'PDV', valor: r.assignment.position.pdv?.name },
              { etiqueta: 'Tipo', valor: r.kind === 'ingreso' ? 'Ingreso' : 'Salida' },
              { etiqueta: 'Hora dispositivo', valor: formatInTimeZone(r.device_ts, TZ, 'dd/MM HH:mm') },
              { etiqueta: 'Estado', valor: STATUS_LABEL[r.status] },
              { etiqueta: 'Nota', valor: r.validation_note },
            ]}
            acciones={
              <>
                <button onClick={() => verFoto(r.photo_path)} className="text-blue-600 underline">
                  Ver foto
                </button>
                {r.lat != null && r.lng != null && (
                  <a className="text-blue-600 underline" target="_blank" rel="noreferrer"
                    href={`https://www.google.com/maps?q=${r.lat},${r.lng}`}>
                    Mapa
                  </a>
                )}
                {sinSalida(r) && (
                  <button onClick={() => abrirSalida(r)} className="text-blue-600 underline">
                    Registrar salida justificada
                  </button>
                )}
                {r.status !== 'pendiente' && (
                  <button
                    onClick={() => { setNote(''); setNoteModal({ mode: 'reabrir', row: r }); }}
                    className="text-blue-600 underline">
                    Reabrir validación
                  </button>
                )}
                {esJefe && (
                  <button onClick={() => abrirBorrado(r)} className="text-red-600 underline">
                    Eliminar registro
                  </button>
                )}
              </>
            }
          />
        ))}
        {absences.map((a) => {
          const ok = a.worker ? justified.has(`${a.worker.id}|${a.date}`) : false;
          return (
            <FilaTarjeta
              key={a.key}
              atenuada
              titulo={a.worker ? `${a.worker.first_names} ${a.worker.last_names}` : '—'}
              estados={
                ok ? (
                  <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-800">
                    Falta justificada
                  </span>
                ) : (
                  <span className="rounded bg-gray-300 px-1.5 py-0.5 text-xs text-gray-800">
                    Falta
                  </span>
                )
              }
              campos={[
                { etiqueta: 'Fecha', valor: a.date },
                { etiqueta: 'PDV', valor: a.pdv?.name },
                { etiqueta: 'Horario', valor: `${a.start_time.slice(0, 5)}–${a.end_time.slice(0, 5)}` },
                { etiqueta: 'Marcados', valor: 'Sin marcados' },
              ]}
            />
          );
        })}
        {visible.length === 0 && absences.length === 0 && (
          <p className="rounded border bg-white p-4 text-center text-sm text-gray-500">
            Sin resultados. Elige campaña y fechas y pulsa Buscar.
          </p>
        )}
      </div>

      <div className="hidden overflow-x-auto lg:block">
        <table className="min-w-full border text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-2">
                {/* Marca todo lo que está a la vista, respetando los filtros. */}
                <input
                  type="checkbox"
                  aria-label="Seleccionar todas las asistencias de la lista"
                  title="Seleccionar todas las de la lista"
                  checked={todasMarcadas}
                  disabled={seleccionables.length === 0}
                  onChange={(e) => alternarTodas(e.target.checked)}
                />
              </th>
              <th className="p-2 text-left">Trabajador</th>
              <th className="p-2">Fecha</th>
              <th className="p-2 text-left">PDV</th>
              <th className="p-2">Tipo</th>
              <th className="p-2">Hora dispositivo</th>
              <th className="p-2 text-left">Alertas</th>
              <th className="p-2">Estado</th>
              <th className="p-2">Foto</th>
              <th className="p-2">Mapa</th>
              <th className="p-2 text-left">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.id} className="border-t">
                <td className="p-2 text-center">
                  {r.status === 'pendiente' && (
                    <input type="checkbox" checked={selected.has(r.id)}
                      onChange={() => toggle(r.id)} />
                  )}
                </td>
                <td className="p-2">
                  {r.assignment.worker.first_names} {r.assignment.worker.last_names}
                </td>
                <td className="p-2 text-center">{r.assignment.position.date}</td>
                <td className="p-2">{r.assignment.position.pdv?.name ?? '—'}</td>
                <td className="p-2 text-center">{r.kind === 'ingreso' ? 'Ingreso' : 'Salida'}</td>
                <td className="p-2 text-center">{formatInTimeZone(r.device_ts, TZ, 'dd/MM HH:mm')}</td>
                <td className="p-2">
                  <div className="flex flex-wrap gap-1">
                    {r.flags.map((f) => {
                      const b = FLAG_BADGES[f] ?? { label: f, cls: 'bg-gray-100 text-gray-800' };
                      return (
                        <span key={f} className={`rounded px-1.5 py-0.5 text-xs ${b.cls}`}>
                          {b.label}
                        </span>
                      );
                    })}
                    {tardanzaJustificada(r) && (
                      <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-800">
                        Tardanza justificada
                      </span>
                    )}
                    <DetalleFotoRepetida row={r} mismaFoto={mismaFoto} />
                    {/* La alerta ES el botón: se toca y se abre la ventana con el hueco que falta,
                        ya con la fecha, la tienda y la persona del turno. */}
                    {sinSalida(r) && (
                      <button
                        onClick={() => puedeCorregir && abrirManual({
                          assignmentId: r.assignment.id,
                          fecha: r.assignment.position.date,
                          pdv: r.assignment.position.pdv?.name ?? 'sin PDV',
                          trabajador: `${r.assignment.worker.first_names} `
                            + r.assignment.worker.last_names,
                          faltan: ['salida'],
                          horaIngreso: r.assignment.position.start_time.slice(0, 5),
                          horaSalida: r.assignment.position.end_time.slice(0, 5),
                        })}
                        disabled={!puedeCorregir}
                        title={puedeCorregir ? 'Registrar la salida a mano' : undefined}
                        className="rounded bg-gray-300 px-1.5 py-0.5 text-xs text-gray-800
                          enabled:cursor-pointer enabled:hover:bg-gray-400">
                        Sin salida{puedeCorregir && ' · completar'}
                      </button>
                    )}
                    {sinIngreso(r) && (
                      <button
                        onClick={() => puedeCorregir && abrirManual({
                          assignmentId: r.assignment.id,
                          fecha: r.assignment.position.date,
                          pdv: r.assignment.position.pdv?.name ?? 'sin PDV',
                          trabajador: `${r.assignment.worker.first_names} `
                            + r.assignment.worker.last_names,
                          faltan: ['ingreso'],
                          horaIngreso: r.assignment.position.start_time.slice(0, 5),
                          horaSalida: r.assignment.position.end_time.slice(0, 5),
                        })}
                        disabled={!puedeCorregir}
                        title={puedeCorregir ? 'Registrar el ingreso a mano' : undefined}
                        className="rounded bg-gray-300 px-1.5 py-0.5 text-xs text-gray-800
                          enabled:cursor-pointer enabled:hover:bg-gray-400">
                        Sin ingreso{puedeCorregir && ' · completar'}
                      </button>
                    )}
                  </div>
                </td>
                <td className="p-2 text-center">{STATUS_LABEL[r.status]}</td>
                <td className="p-2 text-center">
                  <button onClick={() => verFoto(r.photo_path)} className="text-blue-600 underline">
                    Ver foto
                  </button>
                </td>
                <td className="p-2 text-center">
                  {r.lat != null && r.lng != null ? (
                    <a className="text-blue-600 underline" target="_blank" rel="noreferrer"
                      href={`https://www.google.com/maps?q=${r.lat},${r.lng}`}>
                      Mapa
                    </a>
                  ) : ('—')}
                </td>
                <td className="p-2">
                  <div className="flex flex-col items-start gap-1">
                    {puedeCorregir && (
                      <button onClick={() => void abrirEdicion(r)}
                        className="text-blue-600 underline">
                        Editar
                      </button>
                    )}
                    {/* Dos cosas distintas, dos colores: ROJO tranquilo = la marca no existió y
                        la escribió alguien de oficina. AMARILLO = la marca es del campo y se le
                        cambió algo. Confundirlas al leer un cierre es confundir quién respondió
                        por el dato. */}
                    {r.origin === 'manual' && (
                      <span className="rounded border border-red-200 bg-red-50 px-1.5 py-0.5
                        text-xs text-red-700">
                        Manual
                      </span>
                    )}
                    {/* Marcada en la app GARRA (otro sistema) y copiada de noche. Es del campo
                        igual que una del celular: color neutro, no de alerta. */}
                    {r.origin === 'garra' && (
                      <span title="Marcada en la app GARRA y copiada al central cada noche"
                        className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5
                        text-xs text-slate-700">
                        GARRA
                      </span>
                    )}
                    {(correcciones.get(r.id) ?? 0) > 0 && (
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
                        Editado ·{' '}
                        {correcciones.get(r.id) === 1
                          ? '1 cambio'
                          : `${correcciones.get(r.id)} cambios`}
                      </span>
                    )}
                    {sinSalida(r) && (
                      <button onClick={() => abrirSalida(r)} className="text-blue-600 underline">
                        Registrar salida justificada
                      </button>
                    )}
                    {r.status !== 'pendiente' && (
                      <button
                        onClick={() => { setNote(''); setNoteModal({ mode: 'reabrir', row: r }); }}
                        className="text-blue-600 underline">
                        Reabrir validación
                      </button>
                    )}
                    {esJefe && (
                      <button onClick={() => abrirBorrado(r)} className="text-red-600 underline">
                        Eliminar registro
                      </button>
                    )}
                    {r.validation_note && (
                      <span className="text-xs text-gray-500">Nota: {r.validation_note}</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {faltasVisibles.map((a) => {
              const ok = a.worker ? justified.has(`${a.worker.id}|${a.date}`) : false;
              return (
                <tr key={a.key} className="border-t bg-gray-50 text-gray-500">
                  <td className="p-2"></td>
                  <td className="p-2">
                    {a.worker ? `${a.worker.first_names} ${a.worker.last_names}` : '—'}
                  </td>
                  <td className="p-2 text-center">{a.date}</td>
                  <td className="p-2">{a.pdv?.name ?? '—'}</td>
                  <td className="p-2 text-center" colSpan={2}>
                    {a.start_time.slice(0, 5)}–{a.end_time.slice(0, 5)}
                  </td>
                  <td className="p-2">
                    {ok ? (
                      <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-800">
                        Falta justificada
                      </span>
                    ) : (
                      <span className="rounded bg-gray-300 px-1.5 py-0.5 text-xs text-gray-800">
                        Falta
                      </span>
                    )}
                  </td>
                  <td className="p-2 text-center" colSpan={3}>Sin marcados</td>
                  <td className="p-2">
                    {puedeCorregir && (
                      <button
                        onClick={() => abrirManual({
                          assignmentId: a.assignment_id,
                          fecha: a.date,
                          pdv: a.pdv?.name ?? 'sin PDV',
                          trabajador: a.worker
                            ? `${a.worker.first_names} ${a.worker.last_names}`
                            : 'sin trabajador',
                          faltan: ['ingreso', 'salida'],
                          horaIngreso: a.start_time.slice(0, 5),
                          horaSalida: a.end_time.slice(0, 5),
                        })}
                        className="text-blue-600 underline">
                        Completar turno
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {visible.length === 0 && absences.length === 0 && (
              <tr>
                <td colSpan={11} className="p-4 text-center text-gray-500">
                  Sin resultados. Elige campaña y fechas y pulsa Buscar.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {noteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-sm overflow-y-auto rounded bg-white p-4 shadow-lg">
            <h2 className="mb-2 font-semibold">
              {noteModal.mode === 'observar' ? 'Observar asistencias' : 'Reabrir validación'}
            </h2>
            <p className="mb-2 text-sm">
              {noteModal.mode === 'observar'
                ? 'Nota obligatoria (motivo de la observación):'
                : `La asistencia de ${noteModal.row.assignment.worker.first_names} ` +
                  `${noteModal.row.assignment.worker.last_names} del ` +
                  `${noteModal.row.assignment.position.date} volverá a "Pendiente". ` +
                  'Nota obligatoria (motivo de la reapertura):'}
            </p>
            <textarea className="w-full rounded border p-2 text-sm" rows={3} value={note}
              onChange={(e) => setNote(e.target.value)} />
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={cerrarNota} className="rounded border px-3 py-1">
                Cancelar
              </button>
              <button onClick={confirmarNota} disabled={note.trim() === ''}
                className="rounded bg-amber-600 px-3 py-1 text-white disabled:opacity-50">
                {noteModal.mode === 'observar' ? 'Observar' : 'Reabrir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {borrarModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-sm overflow-y-auto rounded bg-white p-4 shadow-lg">
            <h2 className="mb-2 font-semibold text-red-700">Eliminar registro de asistencia</h2>
            <p className="mb-2 text-sm">
              {borrarModal.assignment.worker.first_names}{' '}
              {borrarModal.assignment.worker.last_names} ·{' '}
              {borrarModal.assignment.position.date} ·{' '}
              {borrarModal.kind === 'ingreso' ? 'Ingreso' : 'Salida'}{' '}
              {formatInTimeZone(borrarModal.device_ts, TZ, 'HH:mm')}
            </p>
            {/* Lo que de verdad hay que entender antes de apretar: qué desaparece, qué NO
                desaparece y que el rastro sigue existiendo aunque no se vea. */}
            <p className="mb-2 rounded bg-red-50 p-2 text-sm text-red-800">
              El registro se borra y no se puede deshacer. La asignación no se toca: el turno
              queda como falta y el recurso puede volver a marcar. Adentro queda constancia de
              qué decía la marca y quién la borró.
            </p>
            <label className="block text-sm">
              Motivo (obligatorio)
              <textarea className="mt-1 w-full rounded border p-2 text-sm" rows={3}
                value={motivoBorrado} onChange={(e) => setMotivoBorrado(e.target.value)} />
            </label>
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={() => setBorrarModal(null)} className="rounded border px-3 py-1">
                Cancelar
              </button>
              <button onClick={() => void confirmarBorrado()}
                disabled={loading || motivoBorrado.trim() === ''}
                className="rounded bg-red-600 px-3 py-1 text-white disabled:opacity-50">
                {loading ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {exitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-sm overflow-y-auto rounded bg-white p-4 shadow-lg">
            <h2 className="mb-2 font-semibold">Registrar salida justificada</h2>
            <p className="mb-2 text-sm">
              {exitModal.assignment.worker.first_names} {exitModal.assignment.worker.last_names} ·{' '}
              {exitModal.assignment.position.date} ·{' '}
              {exitModal.assignment.position.start_time.slice(0, 5)}–
              {exitModal.assignment.position.end_time.slice(0, 5)}
            </p>
            <label className="block text-sm">
              Hora de salida (hora de Lima)
              <input type="time" className="mt-1 block rounded border px-2 py-1 text-sm"
                value={exitTime} onChange={(e) => setExitTime(e.target.value)} />
            </label>
            <label className="mt-2 block text-sm">
              Motivo (obligatorio)
              <textarea className="mt-1 w-full rounded border p-2 text-sm" rows={3} value={exitNote}
                onChange={(e) => setExitNote(e.target.value)} />
            </label>
            <p className="mt-2 text-xs text-gray-500">
              Queda registrada como salida manual del coordinador, con tu usuario y el motivo en
              el registro de auditoría.
            </p>
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={() => { setExitModal(null); setExitNote(''); }}
                className="rounded border px-3 py-1">
                Cancelar
              </button>
              <button onClick={registrarSalida}
                disabled={loading || exitTime === '' || exitNote.trim() === ''}
                className="rounded bg-blue-600 px-3 py-1 text-white disabled:opacity-50">
                Registrar salida
              </button>
            </div>
          </div>
        </div>
      )}

      {editRow && form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded bg-white p-4 shadow-lg">
            <h2 className="mb-1 font-semibold">Corregir la marca</h2>
            <p className="mb-3 text-sm text-gray-600">
              {editRow.assignment.worker.first_names} {editRow.assignment.worker.last_names}
              {/* El supervisor recibe el nombre sin DNI: sin número, sin rótulo. */}
              {editRow.assignment.worker.dni && <> · DNI {editRow.assignment.worker.dni}</>}
              <br />
              {editRow.assignment.position.date} ·{' '}
              {editRow.assignment.position.pdv?.name ?? 'sin PDV'} ·{' '}
              {editRow.assignment.position.start_time.slice(0, 5)}–
              {editRow.assignment.position.end_time.slice(0, 5)}
            </p>

            <p className="mb-3 rounded bg-gray-50 p-2 text-xs text-gray-600">
              La <strong>fecha</strong>, el <strong>punto de venta</strong> y el{' '}
              <strong>trabajador</strong> no se corrigen desde acá: vienen de la asignación y se
              cambian desde la programación. Marca solo lo que quieras cambiar.
            </p>

            {/* Cada campo con su casilla: así se cambia solo la foto sin tocar nada más. */}
            <div className="space-y-3">
              <div className="rounded border p-2">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <input type="checkbox" checked={form.cambiarHora}
                    onChange={(e) => setForm({ ...form, cambiarHora: e.target.checked })} />
                  Cambiar la hora
                </label>
                {form.cambiarHora && (
                  <div className="mt-2 pl-6 text-sm">
                    <span className="text-gray-500">
                      Ahora: {formatInTimeZone(editRow.device_ts, TZ, 'dd/MM HH:mm')}
                    </span>
                    <input type="time" className="ml-2 rounded border px-2 py-1"
                      value={form.hora}
                      onChange={(e) => setForm({ ...form, hora: e.target.value })} />
                    <p className="mt-1 text-xs text-gray-500">
                      Hora de Lima. La fecha queda en {editRow.assignment.position.date}.
                    </p>
                  </div>
                )}
              </div>

              <div className="rounded border p-2">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <input type="checkbox" checked={form.cambiarTipo}
                    onChange={(e) => setForm({ ...form, cambiarTipo: e.target.checked })} />
                  Cambiar el tipo de marca
                </label>
                {form.cambiarTipo && (
                  <div className="mt-2 pl-6 text-sm">
                    <span className="text-gray-500">
                      Ahora: {editRow.kind === 'ingreso' ? 'Ingreso' : 'Salida'}
                    </span>
                    <select className="ml-2 rounded border px-2 py-1" value={form.tipo}
                      onChange={(e) => setForm({
                        ...form, tipo: e.target.value as 'ingreso' | 'salida',
                      })}>
                      <option value="ingreso">Ingreso</option>
                      <option value="salida">Salida</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="rounded border p-2">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <input type="checkbox" checked={form.cambiarFoto}
                    onChange={(e) => setForm({ ...form, cambiarFoto: e.target.checked })} />
                  Cambiar la foto
                </label>
                {form.cambiarFoto && (
                  <div className="mt-2 pl-6 text-sm">
                    <button onClick={() => void verFoto(editRow.photo_path)}
                      className="text-blue-600 underline">
                      Ver la foto actual
                    </button>
                    <input type="file" accept="image/*" className="mt-2 block text-sm"
                      onChange={(e) => setForm({ ...form, foto: fotoQueSirve('correccion', e) })} />
                    {rechazoFoto.correccion !== undefined && (
                      <p role="alert" className="mt-1 text-xs text-red-600">{rechazoFoto.correccion}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      La foto actual no se borra: la nueva se guarda aparte y el historial sigue
                      apuntando a la original.
                    </p>
                  </div>
                )}
              </div>

              <div className="rounded border p-2">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <input type="checkbox" checked={form.cambiarNota}
                    onChange={(e) => setForm({ ...form, cambiarNota: e.target.checked })} />
                  Cambiar el comentario
                </label>
                {form.cambiarNota && (
                  <textarea className="mt-2 ml-6 w-[calc(100%-1.5rem)] rounded border p-2 text-sm"
                    rows={2} value={form.nota}
                    onChange={(e) => setForm({ ...form, nota: e.target.value })} />
                )}
              </div>
            </div>

            <label className="mt-3 block text-sm font-medium">
              Motivo de la corrección (obligatorio)
              <textarea className="mt-1 w-full rounded border p-2 text-sm font-normal" rows={2}
                value={form.motivo}
                onChange={(e) => setForm({ ...form, motivo: e.target.value })} />
            </label>

            {historial.length > 0 && (
              <details className="mt-3 text-sm">
                <summary className="cursor-pointer text-gray-600">
                  Historial de correcciones ({historial.length})
                </summary>
                <ul className="mt-2 space-y-1">
                  {historial.map((h, i) => (
                    <li key={i} className="rounded bg-gray-50 p-2 text-xs">
                      <strong>{CAMPO_LABEL[h.campo] ?? h.campo}</strong>{' '}
                      {formatInTimeZone(h.created_at, TZ, 'dd/MM HH:mm')}
                      <br />
                      <span className="text-gray-500">
                        {h.valor_anterior ?? '(vacío)'} → {h.valor_nuevo ?? '(vacío)'}
                      </span>
                      <br />
                      <span className="text-gray-500">Motivo: {h.motivo}</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}

            <p className="mt-3 text-xs text-gray-500">
              Cada cambio queda con tu usuario, el valor anterior y el motivo. Las alertas
              (tardanza, GPS) <strong>no se recalculan</strong>: quedan como las escribió el
              servidor el día de la marca.
            </p>

            <div className="mt-3 flex justify-end gap-2">
              <button onClick={cerrarEdicion} className="rounded border px-3 py-1">
                Cancelar
              </button>
              <button onClick={() => void guardarCorreccion()}
                disabled={
                  loading
                  || form.motivo.trim() === ''
                  || !(form.cambiarHora || form.cambiarTipo || form.cambiarNota
                       || (form.cambiarFoto && form.foto !== null))
                }
                className="rounded bg-blue-600 px-3 py-1 text-white disabled:opacity-50">
                Guardar corrección
              </button>
            </div>
          </div>
        </div>
      )}

      {manual && formManual && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded bg-white p-4 shadow-lg">
            <h2 className="mb-1 font-semibold">
              {manual.faltan.length === 2 ? 'Completar el turno' : 'Completar la marca que falta'}
            </h2>
            <p className="mb-3 text-sm text-gray-600">
              {manual.trabajador}
              <br />
              {manual.fecha} · {manual.pdv}
            </p>

            <p className="mb-3 rounded border border-red-200 bg-red-50 p-2 text-xs text-red-800">
              Lo que se cree acá queda con la etiqueta <strong>Manual</strong>: no lo marcó el
              celular, lo escribiste tú. La <strong>foto es obligatoria</strong> — es la única
              evidencia de que la persona estuvo ahí, y trae hora, fecha y ubicación estampadas.
            </p>

            <div className="space-y-3">
              {(['ingreso', 'salida'] as const).map((kind) => {
                const falta = manual.faltan.includes(kind);
                const sec = formManual[kind];
                return (
                  <div key={kind}
                    className={`rounded border p-2 ${falta ? '' : 'bg-gray-50 opacity-60'}`}>
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <input type="checkbox" checked={sec.activo} disabled={!falta}
                        onChange={(e) => setFormManual({
                          ...formManual, [kind]: { ...sec, activo: e.target.checked },
                        })} />
                      {kind === 'ingreso' ? 'Registrar el ingreso' : 'Registrar la salida'}
                      {!falta && (
                        <span className="text-xs font-normal text-gray-500">
                          — ya existe, se corrige desde «Editar»
                        </span>
                      )}
                    </label>
                    {falta && sec.activo && (
                      <div className="mt-2 space-y-2 pl-6 text-sm">
                        <div>
                          Hora (hora de Lima)
                          <input type="time" className="ml-2 rounded border px-2 py-1"
                            value={sec.hora}
                            onChange={(e) => setFormManual({
                              ...formManual, [kind]: { ...sec, hora: e.target.value },
                            })} />
                          <p className="mt-1 text-xs text-gray-500">
                            Viene del horario programado. Pon la hora real de la foto.
                          </p>
                        </div>
                        <div>
                          Foto (obligatoria)
                          <input type="file" accept="image/*" className="mt-1 block text-sm"
                            onChange={(e) => setFormManual({
                              ...formManual,
                              [kind]: { ...sec, foto: fotoQueSirve(kind, e) },
                            })} />
                          {rechazoFoto[kind] !== undefined && (
                            <p role="alert" className="mt-1 text-xs text-red-600">{rechazoFoto[kind]}</p>
                          )}
                          {sec.foto === null && (
                            <p className="mt-1 text-xs text-red-600">
                              Sin foto esta marca no se va a crear.
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <label className="mt-3 block text-sm font-medium">
              Motivo (obligatorio)
              <textarea className="mt-1 w-full rounded border p-2 text-sm font-normal" rows={2}
                placeholder="Ej.: la tienda no permite celular en sala; la foto la tomó el supervisor"
                value={formManual.motivo}
                onChange={(e) => setFormManual({ ...formManual, motivo: e.target.value })} />
            </label>

            <p className="mt-3 text-xs text-gray-500">
              La fecha, la tienda y la persona salen del turno programado y no se pueden cambiar
              acá. Las marcas quedan <strong>validadas</strong> y entran al cierre. Las alertas de
              tardanza no se calculan: no hubo dispositivo que medir.
            </p>

            <div className="mt-3 flex justify-end gap-2">
              <button onClick={cerrarManual} className="rounded border px-3 py-1">
                Cancelar
              </button>
              <button onClick={() => void guardarManual()}
                disabled={
                  loading
                  || formManual.motivo.trim() === ''
                  || !((formManual.ingreso.activo && formManual.ingreso.foto !== null)
                       || (formManual.salida.activo && formManual.salida.foto !== null))
                }
                className="rounded bg-blue-600 px-3 py-1 text-white disabled:opacity-50">
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
